import { useToastController } from "@tamagui/toast";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider as TanstackQueryClientProvider,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { useCallback, useEffect, useState } from "react";

import { isLocal } from "@/config/env";

/**
 * Dependent providers:
 * - ThemeProvider (for toast)
 */
export default function QueryClientProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [queryClient, setQueryClient] = useState<QueryClient | null>(null);
  const { show } = useToastController();

  const showErrorToast = useCallback(
    (err: Error) => {
      let message = "Something went wrong. Please try again.";
      if (isLocal) {
        message =
          err.message || "An unknown error occurred, please try again later.";
        if (err instanceof AxiosError) {
          message = err.response?.data?.detail || message;
        }
        message =
          typeof message === "string" ? message : JSON.stringify(message);
      }
      show(message, { duration: isLocal ? 10000 : 5000 });
    },
    [show],
  );

  useEffect(() => {
    setQueryClient(
      new QueryClient({
        queryCache: new QueryCache({
          onError: (err, query) => {
            if (query.options.meta?.suppressErrorToast) return;
            showErrorToast(err);
          },
        }),
        mutationCache: new MutationCache({
          onError: (err, _variables, _context, mutation) => {
            if (mutation.options.meta?.suppressErrorToast) return;
            showErrorToast(err);
          },
        }),
      }),
    );
  }, [showErrorToast]);

  if (!queryClient) {
    return null; // Side effect: this prevents a flicker before Tamagui CSS loads
  }

  return (
    <TanstackQueryClientProvider client={queryClient}>
      {children}
    </TanstackQueryClientProvider>
  );
}
